<!--
@component
SchedulePage

See markup below.
-->

<script lang="ts">
  import MatchList from './MatchList.svelte';
  import Calendar from './Calendar/index.svelte';
  import { Block } from '@reuters-graphics/graphics-components';
  import getSchedule from '$utils/getSchedule';
  import type { ScheduleItem } from './types';
  import { filterMatchesByDate, getLastPastMatchTeaser } from './utils';
  import { isPastDay } from '$utils/dates';

  let { data }: { data: any } = $props();

  /** Full schedule, sorted ascending by date. */
  const matchesByDate = $derived<ScheduleItem[]>(
    getSchedule(data.matches, 'asc')
  );

  /** Past entries (calendar day strictly before today, local TZ). */
  const pastMatchesByDate = $derived(
    matchesByDate.filter((d) => isPastDay(d.isoDate))
  );

  /** Upcoming/live entries (today or later, local TZ). */
  const upcomingMatchesByDate = $derived(
    matchesByDate.filter((d) => !isPastDay(d.isoDate))
  );

  /** ISO dates passed to the calendar to mark selectable days. */
  const matchDates = $derived(matchesByDate.map((d) => d.isoDate));

  /** Currently selected date in the calendar (undefined = no filter). */
  let activeDate = $state<Date | undefined>(undefined);

  /** Whether the user has expanded the full past-matches list. */
  let showPastMatches = $state(false);

  /**
   * Matches to render in the main list:
   * - When no date is active, show only upcoming/live (past is rendered
   *   separately in the collapsible block above).
   * - When a date is active, show everything for that day.
   */
  const visibleMatches = $derived(
    activeDate ?
      filterMatchesByDate(matchesByDate, activeDate)
    : upcomingMatchesByDate
  );

  /** Compact preview shown when past matches are collapsed. */
  const pastTeaser = $derived(getLastPastMatchTeaser(pastMatchesByDate));
</script>

<h1 class="visually-hidden">Tournament Schedule</h1>
<!-- Calendar -->
<Block class="fmb-6">
  <Calendar bind:activeDate dates={matchDates} />
</Block>

<Block width="wide">
  {#if activeDate}
    <div
      class="filter-message flex flex-col items-center text-secondary text-sm text-center"
    >
      <span>Showing matches on</span>
    </div>
  {/if}

  <!-- Collapsible past matches (only when not filtering by a specific date) -->
  {#if !activeDate && pastMatchesByDate.length > 0}
    <button
      type="button"
      class="filter-message-past mx-auto"
      class:showPastMatches
      aria-expanded={showPastMatches}
      aria-controls="past-matches-list"
      onclick={() => (showPastMatches = !showPastMatches)}
    >
      {showPastMatches ? 'Hide' : 'Show'} past matches
    </button>

    <ol
      id="past-matches-list"
      class="pastmatches"
      class:showPastMatches
      aria-label="Past matches"
    >
      {#each showPastMatches ? pastMatchesByDate : pastTeaser as item (item.isoDate)}
        <MatchList {item} />
      {/each}
    </ol>
  {/if}

  <!-- Main list: upcoming/live (or all matches on the selected date) -->
  <ol class="matches" aria-label="Scheduled matches">
    {#each visibleMatches as item (item.isoDate)}
      <MatchList {item} />
    {/each}
  </ol>
</Block>

<style lang="scss">
  @use '../../styles/mixins' as mixins;

  ol {
    list-style: none;
    padding: 0;
  }

  .filter-message-past {
    @include mixins.button-primary;
    display: block;
    position: relative;
    margin-bottom: -3rem;
    transition: margin 0.3s ease;
    z-index: 10;

    &.showPastMatches {
      @include mixins.fmy-5;
    }
  }

  .pastmatches {
    height: 7rem;
    overflow: hidden;
    position: relative;
    pointer-events: none;
    transition: all 0.2s ease;

    &:before {
      content: '';
      pointer-events: none;
      transition: opacity 0.3s ease;
      opacity: 1;
      z-index: 1;
      display: block;
      position: absolute;
      background-image: linear-gradient(
        180deg,
        rgba(255, 255, 255, 1) 0%,
        rgba(255, 255, 255, 1) 50%,
        rgba(255, 255, 255, 0.75) 85%,
        rgba(255, 255, 255, 0) 100%
      );
      width: 100%;
      height: 100%;
    }

    &.showPastMatches {
      height: auto;
      pointer-events: all;

      &:before {
        opacity: 0;
      }
    }
  }
</style>
